import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { ApiService } from '../../../core/services/api.service';

interface ApiResponse<T> { status_code: number; message: string; data: T | null; }
interface UserProfile {
  id: number; username: string; full_name: string; email: string | null; role_name: string | null;
  employee_code: string | null; phone_no: string | null; designation: string | null;
  department_name: string | null; profile_picture_url: string | null;
}

@Component({
  selector: 'app-profile-settings',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './profile-settings.component.html',
  styleUrl: './profile-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ProfileSettingsComponent implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  readonly closed = output<void>();
  readonly profile = signal<UserProfile | null>(null);
  readonly photoUrl = signal<string | null>(null);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly message = signal('');
  readonly error = signal('');
  readonly showCurrent = signal(false);
  readonly showNew = signal(false);
  readonly showConfirm = signal(false);
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  private objectUrl: string | null = null;

  readonly initials = computed(() => (this.profile()?.full_name || 'QCI User').split(/\s+/).slice(0, 2).map(x => x[0]).join('').toUpperCase());
  passwordRules() { return [
    { label: 'At least 8 characters', met: this.newPassword.length >= 8 },
    { label: 'One uppercase letter (A–Z)', met: /[A-Z]/.test(this.newPassword) },
    { label: 'One lowercase letter (a–z)', met: /[a-z]/.test(this.newPassword) },
    { label: 'One number (0–9)', met: /\d/.test(this.newPassword) },
    { label: 'One special character', met: /[^A-Za-z0-9]/.test(this.newPassword) },
  ]; }

  constructor() {
    this.api.get<ApiResponse<UserProfile>>('/auth/me').pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => { this.profile.set(response.data); if (response.data?.profile_picture_url) this.loadPhoto(); },
      error: () => this.error.set('Unable to load your profile.'),
    });
  }

  close(): void { this.closed.emit(); }

  choosePhoto(input: HTMLInputElement): void { input.click(); }

  uploadPhoto(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      this.error.set('Choose a JPG or PNG image up to 2 MB.'); return;
    }
    const form = new FormData(); form.append('file', file);
    this.saving.set(true); this.clearNotices();
    this.api.post<ApiResponse<{ profile_picture_url: string }>>('/auth/profile-picture', form).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => { this.message.set('Profile picture updated.'); this.loadPhoto(); },
      error: error => this.error.set(error.error?.message || 'Unable to upload profile picture.'),
    });
  }

  removePhoto(): void {
    if (!this.photoUrl()) return;
    this.saving.set(true); this.clearNotices();
    this.api.delete<ApiResponse<null>>('/auth/profile-picture').pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => { this.revokePhoto(); this.message.set('Profile picture removed.'); },
      error: () => this.error.set('Unable to remove profile picture.'),
    });
  }

  updatePassword(): void {
    this.clearNotices();
    if (!this.currentPassword || !this.passwordRules().every(rule => rule.met)) { this.error.set('Complete all password requirements.'); return; }
    if (this.newPassword !== this.confirmPassword) { this.error.set('New password and confirmation do not match.'); return; }
    this.saving.set(true);
    this.api.patch<ApiResponse<null>>('/auth/change-password', { current_password: this.currentPassword, new_password: this.newPassword })
      .pipe(finalize(() => this.saving.set(false))).subscribe({
        next: () => {
          this.currentPassword = this.newPassword = this.confirmPassword = '';
          this.auth.logout();
          void this.router.navigate(['/auth/login'], { queryParams: { reason: 'password-changed' }, replaceUrl: true });
        },
        error: error => this.error.set(error.error?.message || 'Unable to update password.'),
      });
  }

  ngOnDestroy(): void { this.revokePhoto(); }
  private loadPhoto(): void { this.api.download('/auth/profile-picture').subscribe({ next: blob => { this.revokePhoto(); this.objectUrl = URL.createObjectURL(blob); this.photoUrl.set(this.objectUrl); } }); }
  private revokePhoto(): void { if (this.objectUrl) URL.revokeObjectURL(this.objectUrl); this.objectUrl = null; this.photoUrl.set(null); }
  private clearNotices(): void { this.message.set(''); this.error.set(''); }
}
